import { CircleLayer, HeatmapLayer } from '@trimblemaps/trimblemaps-js';
import { Theme, alpha } from '@mui/material';

import { FeatureTypes, MapLayers, MapSources } from '../enums';
import { isVisibleUtil } from '../mapUtils';

/**
 * Creates a layer to display circles on the map for each Point and 'featureType' of 'areaCenter' in the given map source.
 * Adds the density areas center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param minDensityAmount Minimum density amount to be used for the radius scale
 * @param maxDensityAmount Maximum density amount to be used for the radius scale
 * @param theme MUI theme to use for styling
 * @param isVisible Indicator if the map layer is visible
 */
export const addDensityAreasHeatmapLayer = ({
    map,
    mapSource,
    minDensityAmount,
    maxDensityAmount,
    theme,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    minDensityAmount: number;
    maxDensityAmount: number;
    theme: Theme;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: HeatmapLayer = {
        id: MapLayers.densityAreasHeatmapLayer,
        type: 'heatmap',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            // Increase the heatmap weight based on property densityAmount
            'heatmap-weight': [
                'interpolate',
                ['linear'],
                ['+', 15, ['get', 'densityAmount']],
                minDensityAmount,
                0,
                maxDensityAmount,
                1,
            ],
            // Increase the heatmap color weight by zoom level
            // heatmap-intensity is a multiplier on top of heatmap-weight
            'heatmap-intensity': [
                'interpolate',
                ['linear'],
                ['zoom'],
                0,
                1,
                9,
                3,
            ],
            // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
            // Begin color ramp at 0-stop with a 0-transperancy color to create a blur-like effect.
            'heatmap-color': [
                'interpolate',
                ['linear'],
                ['heatmap-density'],
                0,
                alpha(theme.palette.info.main, 0),
                0.1,
                theme.palette.info.light,
                0.2,
                theme.palette.success.light,
                0.3,
                theme.palette.success.main,
                0.4,
                theme.palette.success.dark,
                0.5,
                theme.palette.warning.light,
                0.6,
                theme.palette.warning.main,
                0.7,
                theme.palette.warning.dark,
                0.8,
                theme.palette.error.light,
                0.9,
                theme.palette.error.main,
                1,
                theme.palette.error.dark,
            ],
            // Increase the heatmap radius based property densityAmount
            'heatmap-radius': [
                'interpolate',
                ['linear'],
                ['+', 15, ['get', 'densityAmount']],
                minDensityAmount,
                5,
                maxDensityAmount,
                40,
            ],
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.AreaCenter],
        ],
    };

    if (map.getLayer(MapLayers.densityAreasHeatmapLayer) === undefined) {
        map.addLayer(layer);
    } else if (layer.paint) {
        map.setPaintProperty(MapLayers.densityAreasHeatmapLayer, 'heatmap-weight', layer.paint['heatmap-weight']);
        map.setPaintProperty(MapLayers.densityAreasHeatmapLayer, 'heatmap-radius', layer.paint['heatmap-radius']);
    }
};

/**
 * Creates a layer to add circles on the map for each Point and 'featureType' of 'areaCenter' in the given map source.
 * Adds the density areas center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param minDensityAmount Minimum density amount to be used for the radius scale
 * @param maxDensityAmount Maximum density amount to be used for the radius scale
 * @param isVisible Indicator if the map layer is visible
 */
export const addDensityAreasCenterLayer = ({
    map,
    mapSource,
    minDensityAmount,
    maxDensityAmount,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    minDensityAmount: number;
    maxDensityAmount: number;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.densityAreasCenterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-opacity': 0,
            'circle-stroke-opacity': 0,
            'circle-radius': [
                'interpolate',
                ['linear'],
                ['+', 15, ['get', 'densityAmount']],
                minDensityAmount,
                5,
                maxDensityAmount,
                40,
            ],
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.AreaCenter],
        ],
    };
    if (map.getLayer(MapLayers.densityAreasCenterLayer) === undefined) {
        map.addLayer(layer);
    } else if (layer.paint) {
        map.setPaintProperty(MapLayers.densityAreasCenterLayer, 'circle-radius', layer.paint['circle-radius']);
    }
};
